import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ContactType, ContactTypeEnum, Device, Group, Individual, Pageable} from 'src/app/models';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {
    ContactTypeService,
    CountryCodeService,
    DataService,
    DevicesService,
    GroupsService,
    IndividualsService,
    MessagesService,
    SnackbarService
} from 'src/app/services';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import parseMax from 'libphonenumber-js/max';
import {NG_VALUE_ACCESSOR, NgModel} from '@angular/forms';
import {AutocompleteComponent, ButtonConfig, ChipPopupComponent} from 'src/app/components';
import {DeleteDeviceDialogComponent} from './device-delete/device-delete.component';
import {RouteGuard} from 'src/app/guard/route-guard';
import {safeGet} from 'src/app/utilities/objects';
import {FaqComponent} from 'src/app/screens';
import {MatDialog} from '@angular/material/dialog';

@Component({
    selector: 'app-devices',
    templateUrl: './devices.component.html',
    styleUrls: ['./devices.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DevicesComponent
        }
    ]
})

export class DevicesComponent implements OnInit {
    deviceSearch = '';
    i = 18;
    autocompleteDevices: Device[] = [];
    autocompleteDevicesFormatted: string[] = [];
    selectedDevice = new Device();
    requestedDevice = new Device();
    isMobileResolution: boolean;
    filter = '';
    pageNum = 1;
    countryCode = '';
    unformatedPhone = '';
    deviceChange = new Subject<string>();
    currentIndividual: any;
    members: any;
    resultsReady = false;
    hourglass = false;
    isAdmin: boolean;
    errorText = '';
    error = false;
    buttonsDisabled = false;
    teamsSaveAllowed = false;
    teamsPrivateError = false;

    isOwner = false;
    owner: Individual;
    user: Individual;
    ownerSearch: String;
    autocompleteIndividual: Individual[] = [];
    deviceOwnerChange = new Subject<string>();
    contactTypes = [ContactTypeEnum.SMS, ContactTypeEnum.Email, ContactTypeEnum.Webhook];
    contactTypesFull: ContactType[] = [];
    buttonConfig: ButtonConfig;

    currentUser: any;
    invalidUser = false;
    fullReady = false;

    isNewDevice = false;
    hasOwnerChanged = false;

    //grid variables
    header = ['ID', 'Name'];
    width = [50, 9999];
    userCreatedDevicesSetting = true;
    showGridFilter = false;
    devices: Device[] = [];
    page = {
        limit: 20,
        count: 0,
        offset: 0,
        orderBy: 'name',
        orderDir: 'asc'
    };
    hasSelectedDevice = false;
    oldPage = 0;

    @ViewChild('autoComplete', {static: true}) autoComplete: AutocompleteComponent;
    @ViewChild('nameInput') nameInput: NgModel;
    @ViewChild('deviceNameInput') deviceNameInput: NgModel;
    @ViewChild('ownerInput') ownerInput: NgModel;
    @ViewChild('ownerInputCtrl', {read: ElementRef}) ownerInputCtrl: ElementRef;
    @ViewChild('contactTypeInput') contactTypeInput: NgModel;
    @ViewChild('smsInput') smsInput: NgModel;
    @ViewChild('emailInput') emailInput: NgModel;
    @ViewChild('webhookInput') webhookInput: NgModel;
    @ViewChild('teamsInput') teamsInput: NgModel;

    constructor(public deviceService: DevicesService,
                public groupsService: GroupsService,
                public messagesService: MessagesService,
                private snackbarService: SnackbarService,
                private dialog: MatDialog,
                private dataService: DataService,
                private individualsService: IndividualsService,
                private countryCodeService: CountryCodeService,
                private contactTypeService: ContactTypeService,
                private guard: RouteGuard
    ) {
    }

    ngOnInit() {
        this.error = false;
        this.buttonConfig = {
            enable: true,
            icon: 'add_circle',
            label: 'Add Icon',
            color: 'primary',
            id: 'DeviceAdd',
            class: 'flex',
        };

        // Init country codes
        this.countryCodeService.getAllCountryCodes();
        this.dataService.getUserInfo().subscribe({
            next: res => {
                this.currentUser = res;
                this.currentIndividual = res;
                this.isAdmin = this.currentUser.isAdmin;

                this.individualsService.getIndividuals(res['email']).subscribe(individual => {
                    this.user = individual[0];
                }).add(() => {
                    this.getDevicesForGrid();
                    this.fullReady = true;
                });
            }, error: () => {
                this.error = true;
                this.errorText = 'Error getting user information from alerting - if the error persists, please message the SS&C Alerting Experience chat channel';
            }
        });

        this.dataService.getSetting("FF_TeamsLogicApp").subscribe({
            next: (res: boolean) => {
                if (res && !this.contactTypes.includes(ContactTypeEnum.Teams)) {
                    this.contactTypes.push(ContactTypeEnum.Teams);
                }
                this.contactTypeService.getContactTypes().subscribe({
                    next: (res) => {
                        this.contactTypesFull = res;
                    }
                });
            }
        });

        this.isMobileResolution = window.innerWidth < 768;
        this.setupOnChangeSearches();
    }

    markAsTouched() {
        this.nameInput.control.markAsTouched();
        this.contactTypeInput.control.markAsTouched();
        this.resolveAddressControl().markAsTouched();
    }

    isValid() {
        this.markAsTouched();
        return this.nameInput.control.valid
            && this.contactTypeInput.control.valid
            && this.resolveAddressControl().valid;
    }

    resolveAddressControl() {
        switch (this.contactTypeInput.control.value) {
            case ContactTypeEnum.SMS:
                return this.smsInput.control;
            case ContactTypeEnum.Email:
                return this.emailInput.control;
            case ContactTypeEnum.Webhook:
                return this.webhookInput.control;
            case ContactTypeEnum.Teams:
                return this.teamsInput.control;
        }
    }

    setupOnChangeSearches() {
        const delay = 750;
        this.deviceChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getAutocompleteDevices(term.trim(), 'all'));
        this.deviceOwnerChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getAutocompleteOwners(term.trim()));
    }

    onDeviceChanged($event: any) {
        this.isNewDevice = false;
        this.hasOwnerChanged = false;
        if ($event.data === undefined) {
            this.deviceSearch = $event;
            this.deviceChange.next($event);
        }
    }

    deleteDevice() {
        const dialogRef = this.dialog.open(DeleteDeviceDialogComponent, {
            width: '325px',
            height: '205px',
            panelClass: 'confirmation',
            data: {name: this.selectedDevice.name}
        });
        dialogRef.afterClosed().subscribe(result => {
            this.displayProcessing(true);
            if (result) {
                this.deviceService.deleteDevice(this.selectedDevice.id).subscribe(() => {
                    this.snackbarService.openSnackBar('Channel Deleted Successfully', false);
                    this.cancel();
                });
            } else {
                this.displayProcessing(false);
            }
        });
    }

    closeDevice() {
        this.hasSelectedDevice = false;
        this.resultsReady = false;
    }

    getAutocompleteDevices(filterValueName: string, activeValue: string) {
        if (filterValueName != null && filterValueName.length > 1) {
            this.filter = filterValueName;
            this.deviceService.getDevicesByIdentifierIgnoreWhitespace(filterValueName).subscribe((devices: Device[]) => {
                this.autocompleteDevices = devices;
                this.autocompleteDevicesFormatted = [];
                this.autocompleteDevices.forEach(e => {
                    this.autocompleteDevicesFormatted.push(e.name);
                });
            });
        } else {
            this.autocompleteDevices = [];
            this.autocompleteDevicesFormatted = [];
        }
    }

    getAutocompleteOwners(term: string): void {
        this.autocompleteIndividual = [];
        if (term.length >= 2) {
            this.individualsService.getIndividuals(term).subscribe(individual => {
                individual.forEach(e => this.autocompleteIndividual.push(e));
            });
        }
    }

    load(page) {
        if (page.data === undefined) {
            this.pageNum = page;
            this.deviceService.getDevicesByNamePage(this.filter, page * 15).subscribe({
                next: devices => {
                    // @ts-ignore
                    this.autocompleteDevices = devices.content;
                    this.autocompleteDevicesFormatted = [];
                    this.autocompleteDevices.forEach(e => {
                        this.autocompleteDevicesFormatted.push(e.name);
                    });
                }, error: () => this.snackbarService.openSnackBar('There was an error retrieving data, try again', true)
            });
        }
    }

    newDevice() {
        this.hasSelectedDevice = true;
        this.isNewDevice = true;
        this.unformatedPhone = '';
        this.displayProcessing(true);
        this.deviceSearch = this.deviceSearch.trim();
        if (this.deviceSearch.length > 0) {
            this.dataService.getDeviceNameAvailability(this.deviceSearch).subscribe(isValid => {
                if (isValid) {
                    this.initializeDevice();
                    this.selectedDevice.generateDeviceName();
                } else {
                    this.snackbarService.openSnackBar('A channel with this name already exists.', true);
                    this.hasSelectedDevice = false;
                    this.displayProcessing(false);
                }
                this.deviceSearch = '';
            });
        } else {
            this.initializeDevice();
            this.deviceSearch = '';
        }
    }

    initializeDevice() {
        this.selectedDevice = new Device();
        if (this.isNewDevice === true) {
            this.addDeviceOwner(undefined, this.user);
        }
        if (this.deviceSearch.length > 0) {
            this.selectedDevice.name = this.deviceSearch;
            this.selectedDevice.generateDeviceName();
        }
        this.selectedDevice.contact_type = ContactTypeEnum.SMS;
        this.selectedDevice.contact_type_id = this.getContactTypeId();
        this.teamsSaveAllowed = true;
        this.countryCode = '1';
        this.members = [];
        this.displayProcessing(false);
        this.resultsReady = true;
        this.autoComplete.clear();
    }

    displayProcessing(toggle: boolean): void {
        this.hourglass = toggle;
        this.buttonsDisabled = toggle;
    }

    selectDevice(event: MatAutocompleteSelectedEvent) {
        this.displayProcessing(true);
        this.deviceChange.next('');
        this.error = false;
        this.errorText = '';
        this.requestedDevice = this.getDeviceByName(event.option.value);
        this.selectedDevice = Object.assign(new Device(), this.requestedDevice);
        this.clearSearch();
        this.deviceService.getMembership(this.selectedDevice.id)
            .subscribe(members => this.members = members)
            .add(() => this.selectDeviceHelper());
        this.hasSelectedDevice = true;
    }

    selectDeviceHelper() {
        let error_message = '';
        this.teamsSaveAllowed = true;
        if (this.selectedDevice.contact_type === ContactTypeEnum.SMS) {
            const formatted_value = parseMax(this.selectedDevice.address);
            if (formatted_value == undefined) {
                error_message = `Unable to parse SMS address '${this.selectedDevice.address}' `;
            } else {
                this.unformatedPhone = formatted_value.nationalNumber.toString();
                this.countryCode = formatted_value.countryCallingCode.toString();
            }
        } else if (this.selectedDevice.contact_type === ContactTypeEnum.Chat) {
            this.selectedDevice.contact_type = ContactTypeEnum.SMS;
            error_message = 'Mattermost Decommissioned - Please Enter Valid Contact Info ';
        }
        // default to current user if device has no owner and display caution hint
        if (!this.selectedDevice.individual_id) {
            this.addDeviceOwner(undefined, this.user);
            this.displayProcessing(false);
            this.resultsReady = true;
        } else {
            this.individualsService.getIndividual(this.selectedDevice.individual_id).subscribe({
                next: (individual: Individual) => {
                    this.owner = individual;
                    if (this.user != null && individual.associate_email && individual.associate_email.toUpperCase() === this.user.associate_email.toUpperCase()) {
                        this.isOwner = true;
                    }
                }, error: () => {
                    this.owner = undefined;
                    this.selectedDevice.individual_id = null;
                    error_message +=  `${error_message != '' ? ' & ' : ''} Failed to locate owner with id: ${this.selectedDevice.individual_id}`
                }
            }).add(() => {
                this.displayProcessing(false);
                this.resultsReady = true;
                if (error_message != '') {
                    this.snackbarService.openSnackBar(error_message, true);
                }
            });
        }
    }

    getDeviceByName(name: string): Device {
        return new Device(this.autocompleteDevices.find(device => device.name === name));
    }

    updateName(): void {
        this.selectedDevice.generateDeviceName();
        this.deviceNameInput.control.markAsTouched();
    }

    updateNumber(): void {
        this.smsInput.control.markAsTouched();
        if (this.smsInput.control.valid) {
            this.selectedDevice.address = '+' + this.countryCode + this.smsInput.control.value;
        }
    }

    updateTeamsLink(): void {
        this.teamsSaveAllowed = false;
        this.teamsPrivateError = this.selectedDevice.address.includes("ngc=true");
    }

    save() {
        this.displayProcessing(true);
        this.autocompleteDevicesFormatted = [];

        if (this.selectedDevice.individual_id != undefined) {
            if (this.isValid()) {
                this.dataService.getDeviceNameAvailability(this.selectedDevice.name).subscribe({
                    next: res => {
                        if (!this.selectedDevice.id && res) {
                            if (this.selectedDevice.contact_type === ContactTypeEnum.SMS) {
                                this.selectedDevice.address = '+' + this.countryCode + this.unformatedPhone;
                            }
                            this.deviceService.createDevice(this.selectedDevice).subscribe({
                                next: () => {
                                    this.snackbarService.openSnackBar('Channel Created Successfully', false);
                                    this.cancel();
                                }, error: e => this.snackbarService.openSnackBar('The channel could not be created: ' + e.error.message, true)
                            }).add(() => this.displayProcessing(false));
                        } else if (
                            this.selectedDevice.id
                            && (this.selectedDevice.device_name === this.requestedDevice.device_name || (this.selectedDevice.device_name !== this.requestedDevice.device_name && res))) {
                            this.deviceService.updateDevice(this.selectedDevice).subscribe({
                                next: () => {
                                    this.snackbarService.openSnackBar('Channel Updated Successfully', false);
                                    this.cancel();
                                }, error: () => this.snackbarService.openSnackBar('The channel could not be updated, please resolve the errors below', true)
                            }).add(() => this.displayProcessing(false));
                        } else {
                            this.snackbarService.openSnackBar('Please enter the values below', true);
                            this.displayProcessing(false);
                        }
                    }, error: () => {
                        this.snackbarService.openSnackBar('Error contacting server, try again', true);
                        this.displayProcessing(false);
                    }
                });
            } else {
                this.snackbarService.openSnackBar('The channel could not be updated, please resolve the errors below', true);
                this.displayProcessing(false);
            }
        } else {
            this.snackbarService.openSnackBar('Channel Needs An Owner', true);
            this.displayProcessing(false);
        }
        this.getDevicesForGrid();
    }

    cancel(): void {
        this.displayProcessing(true);
        this.resultsReady = false;
        this.unformatedPhone = '';
        this.selectedDevice = new Device();
        this.autocompleteDevicesFormatted = [];
        this.displayProcessing(false);
        this.hasSelectedDevice = false;
        this.getDevicesForGrid();
    }

    teamsLinkTest() {
        this.messagesService.validateTeamsAddress(this.selectedDevice.address).subscribe({
            next: (success: boolean) => {
                if (success) {
                    this.snackbarService.openSnackBar('Test message was sent successfully', false);
                    this.teamsSaveAllowed = true;
                } else {
                    this.snackbarService.openSnackBar('Test message failed to send', true);
                    this.teamsSaveAllowed = false;
                }
            }
        });
    }

    contactTypeChanged(): void {
        this.selectedDevice.address = '';
        this.selectedDevice.contact_type_id = this.getContactTypeId();
        this.teamsSaveAllowed = this.selectedDevice.contact_type !== ContactTypeEnum.Teams;
    }

    getContactTypeId(): number {
        return this.contactTypesFull.find(c => c.contact_type.toUpperCase() === this.selectedDevice.contact_type.toUpperCase()).contact_type_id;
    }

    openIndividualPopup(indID: any): void {
        this.individualsService.getIndividual(indID.id != undefined ? indID.id : indID).subscribe((individual: Individual) => {
            this.dialog.open(ChipPopupComponent, {
                width: '65%',
                maxHeight: '90%',
                data: {object: individual, type: 'INDIVIDUAL'}
            });
        });
    }

    openGroupPopup(groupID: any): void {
        this.groupsService.getGroup(groupID).subscribe((group: Group) => {
            this.dialog.open(ChipPopupComponent, {
                width: '85%',
                maxHeight: '90%',
                data: {object: group, type: 'GROUP'}
            });
        });
    }

    hasEditAccess(): boolean {
        if (this.guard.isAdmin) {
            this.isAdmin = true;
        }
        this.isOwner = this.user != null && this.owner ? (this.owner.id === this.user.id) : false;
        return this.isAdmin || this.isOwner || this.selectedDevice.individual_id == null || this.hasOwnerChanged;
    }

    isDeactivated(): boolean {
        if (this.selectedDevice.active === false) {
            if (safeGet('id', this.selectedDevice) == undefined) {
                return true;
            }
        }
        return false;
    }

    onDeviceOwnerChanged($event: any): void {
        this.hasOwnerChanged = true;
        if ($event.data === undefined) {
            this.ownerSearch = $event;
            this.deviceOwnerChange.next($event);
        }
    }

    addDeviceOwner(newOwnerId: number, fullOwner?: Individual): void {
        if (fullOwner != undefined && !this.isNewDevice) {
            this.snackbarService.openSnackBar('Channel Needs An Owner', true);
            this.owner = null;
        } else if (fullOwner != undefined && this.isNewDevice) {
            this.owner = fullOwner;
        } else {
            this.owner = this.autocompleteIndividual.find(i => i.id === newOwnerId);
        }
        if (this.owner != undefined && safeGet('individual_id', this.selectedDevice) !== this.owner.id) {
            this.selectedDevice.individual_id = this.owner.id;
        }
        this.deviceOwnerChange.next('');
        if (newOwnerId) {
            this.clearOwnerSearch();
        }
    }

    openDevice(device: Device) {
        this.displayProcessing(true);
        this.deviceChange.next('');
        this.error = false;
        this.errorText = '';
        this.requestedDevice = device;
        this.selectedDevice = Object.assign(new Device(), this.requestedDevice);
        this.deviceService.getMembership(device.id)
            .subscribe(members => this.members = members)
            .add(() => this.selectDeviceHelper());
        this.hasSelectedDevice = true;
    }

    onActivate(event) {
        if (event.type == 'click') {
            this.openDevice(event.row);
        }
    }

    pageCallback(pageInfo: { count?: number, pageSize?: number, limit?: number, offset?: number }) {
        this.page.offset = pageInfo.offset;
        this.getDevicesForGrid();
    }

    sortCallback(sortInfo: { sorts: { dir: string, prop: string }[], column: {}, prevValue: string, newValue: string }) {
        this.page.orderDir = sortInfo.sorts[0].dir;
        this.page.orderBy = sortInfo.sorts[0].prop;
        this.getDevicesForGrid();
    }

    filterChange(value: boolean) {
        this.userCreatedDevicesSetting = value;
        let old = this.oldPage;
        this.oldPage = this.page.offset;
        this.page.offset = old;
        this.getDevicesForGrid();
    }

    getDevicesForGrid() {
        if (this.user) {
            this.deviceService.getDevicesByOwnerPage(this.page, this.user.id).subscribe((res: Pageable<Device>) => {
                if (res.total_elements === 0) {
                    this.userCreatedDevicesSetting = false;
                    this.showGridFilter = false;
                } else {
                    this.showGridFilter = true;

                    if (this.userCreatedDevicesSetting) {
                        this.page.count = res.total_elements;
                        this.devices = res.content;
                    }
                }
            }).add(() => {
                if (!this.userCreatedDevicesSetting) {
                    this.getAllDevicesPage();
                }
            });
        } else {
            this.userCreatedDevicesSetting = false;
            this.showGridFilter = false;
            this.getAllDevicesPage();
        }
    }

    getAllDevicesPage() {
        this.deviceService.getDevicesPage(this.page).subscribe((res: Pageable<Device>) => {
            this.page.count = res.total_elements;
            this.devices = res.content;
        });
    }

    ownerSelected(): boolean {
        return safeGet('id', this.owner) != null;
    }

    removeDeviceOwner(): void {
        if (!this.hasEditAccess()) {
            return;
        }
        this.owner = new Individual();
        this.selectedDevice.individual_id = null;
        this.ownerInput.control.markAsTouched();
    }

    clearOwnerSearch() {
        this.ownerInput.control.setValue('');
        this.ownerInputCtrl.nativeElement.value = '';
    }

    openFAQ() {
        this.dialog.open(FaqComponent, {
            width: '95%',
            height: '95%',
            data: {
                category: 'Channels',
                question: 'How do I create an incoming webhook for my Teams channel?'
            }
        });
    }

    private clearSearch() {
        this.autocompleteDevicesFormatted = [];
        this.deviceSearch = '';
    }
}
